/**
 * useDownloadQuote — keep a ``POST download/quote`` for a set of model versions while a surface
 * that can start the download is open (the Configure deployment modal in download mode), so the
 * price is on screen before the Download click. SaaS with billing on only; inert elsewhere.
 */
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/features/auth/AuthContext';
import { fetchDownloadQuote } from '../api/billingApi';
import { dedupeQuoteItems } from './useDownloadQuoteGate';

const USD_WHOLE = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const formatCents = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    return Number.isInteger(amount) ? USD_WHOLE.format(amount) : USD.format(amount);
};

/**
 * Footer sentence for a quote (Figma 9952:24284): the free-downloads rule while nothing is
 * charged, the amount otherwise. ``null`` without a quote.
 */
export function downloadQuoteFooterText(quote) {
    if (!quote) return null;
    if ((Number(quote.total_cents) || 0) === 0) {
        return `Your first ${quote.free_limit} downloads are free. Standard download cost: ${formatCents(quote.paid_price_cents)}.`;
    }
    return `Estimated model download cost: ${formatCents(quote.total_cents)}`;
}

/** "Download 1 of 2 Free" while this bundle uses free downloads; *fallback* otherwise. */
export function downloadButtonLabel(quote, fallback = 'Download') {
    if (!quote || (Number(quote.total_cents) || 0) > 0) return fallback;
    const newlyFree = (quote.items || []).filter((item) => item.pricing === 'free').length;
    if (!newlyFree || !Number.isFinite(quote.free_limit)) return fallback;
    const usedAfterThis = quote.free_limit - (Number(quote.free_downloads_remaining) || 0);
    return `Download ${usedAfterThis} of ${quote.free_limit} Free`;
}

export function useDownloadQuote(items, { enabled = true } = {}) {
    const { isSaas, billingEnabled } = useAuth() || {};
    const deduped = useMemo(() => dedupeQuoteItems(items), [items]);
    const itemsKey = useMemo(
        () => JSON.stringify(deduped.map(({ llm_usecase_id, version }) => ({ llm_usecase_id, version }))),
        [deduped]
    );
    const quoted = isSaas === true && billingEnabled === true && enabled && deduped.length > 0;
    const [state, setState] = useState({ quote: null, loading: false, error: null });

    useEffect(() => {
        if (!quoted) {
            setState({ quote: null, loading: false, error: null });
            return undefined;
        }
        let cancelled = false;
        setState((current) => ({ ...current, loading: true, error: null }));
        fetchDownloadQuote(JSON.parse(itemsKey))
            .then((quote) => {
                if (!cancelled) setState({ quote, loading: false, error: null });
            })
            .catch((error) => {
                if (!cancelled) setState({ quote: null, loading: false, error });
            });
        return () => {
            cancelled = true;
        };
    }, [quoted, itemsKey]);

    return { ...state, quoted };
}

export default useDownloadQuote;
